import { useRef, useState } from "react";
import SectionHeader from "../SectionHeader";
import Checklist from "../Checklist";
import Skeleton from "Components/Common/Skeleton/Skeleton";
import ProgressBar from "Components/Common/ProgressBar/ProgressBar";
import EmptyState from "Components/Common/EmptyState/EmptyState";
import EmptyPreconReportIcon from "Assets/Img/EmptyPreconReportIcon";
import Input from "Components/Common/Input/Input";

interface Completion {
  label: string;
  value: number;
}

interface DocumentItem {
  id?: string;
  label: string;
  status: string;
  icon?: string;
  sub?: string;
  action?: (() => void) | boolean | null;
  actionLabel?: string;
}

interface QaItem {
  id?: string;
  label: string;
  status: string;
  icon?: string;
  sub?: string;
}

interface PreconSectionProps {
  isActive: boolean;
  completion: Completion;
  documents: DocumentItem[];
  qaItems: QaItem[];
  onDocUpload?: (itemId: string, file: File) => Promise<void>;
  loading?: boolean;
  notFound?: boolean;
}

const PreconSection = ({
  isActive,
  completion,
  documents,
  qaItems,
  onDocUpload,
  loading = true,
  notFound,
}: PreconSectionProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const pendingItemId = useRef<string | null>(null);
  const [uploadingId, setUploadingId] = useState<string | null>(null);

  const handleActionClick = (itemId: string) => {
    pendingItemId.current = itemId;
    inputRef.current?.click();
  };

  const handleFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    const file = e.target.files?.[0];
    const itemId = pendingItemId.current;
    e.target.value = "";
    pendingItemId.current = null;
    if (!file || !itemId || !onDocUpload) return;
    setUploadingId(itemId);
    await onDocUpload(itemId, file);
    setUploadingId(null);
  };

  if (notFound) {
    return (
      <div className={`section ${isActive ? "active" : ""}`} id="sec-precon">
        <EmptyState
          icon={<EmptyPreconReportIcon />}
          title="Pre-Construction Not Yet Set Up"
          subtitle="The admin has not yet configured the pre-construction checklist for this project. Check back once it has been set up."
          noBorder
        />
      </div>
    );
  }

  return (
    <div className={`section ${isActive ? "active" : ""}`} id="sec-precon">
      <SectionHeader
        title="Pre-Construction Checklist"
        subtitle="Required documentation and Q&A before the first draw can be released. CSC will review submissions and author the pre-construction report."
      />

      <Input
        ref={inputRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
        style={{ display: "none" }}
        onChange={handleFileChange}
      />

      <div className="card">
        {loading ? (
          <>
            <Skeleton width={200} height={14} style={{ marginBottom: 8 }} />
            <Skeleton width="100%" height={8} borderRadius={4} />
          </>
        ) : (
          <>
            <div className="progRow">
              <div className="cardTitle">Overall Completion</div>
              <span className="progVal">{completion.label} complete</span>
            </div>
            <ProgressBar value={completion.value} />
          </>
        )}
      </div>

      {loading ? (
        <div className="card">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton
              key={i}
              height={36}
              borderRadius={6}
              style={{ marginBottom: 8 }}
            />
          ))}
        </div>
      ) : (
        <Checklist
          title="Required Documents"
          items={documents.map((item) => ({
            ...item,
            action: item.action
              ? uploadingId === item.id
                ? () => {}
                : item.id
                  ? () => handleActionClick(item.id!)
                  : undefined
              : undefined,
            actionLabel:
              uploadingId === item.id ? "Uploading…" : item.actionLabel,
          }))}
        />
      )}

      {loading ? (
        <div className="card">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton
              key={i}
              height={36}
              borderRadius={6}
              style={{ marginBottom: 8 }}
            />
          ))}
        </div>
      ) : (
        <Checklist title="Pre-Construction Q&A" items={qaItems} />
      )}
    </div>
  );
};

export default PreconSection;
